import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getTasks, getProjects, getUserNames, createTask, updateTask, deleteTask } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Edit, Trash2, Clock, Search, Calendar, AlertTriangle, Timer, LayoutGrid, List, Target, X, Building2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import TaskBoard from '../components/TaskBoard';
import TaskDetailDialog from '../components/TaskDetailDialog';

const Tasks = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const projectFilter = searchParams.get('project');
  
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [viewMode, setViewMode] = useState('board'); // 'board' or 'list'
  const [searchTerm, setSearchTerm] = useState('');
  const [projectStatusFilter, setProjectStatusFilter] = useState('active'); // Default to active projects
  const [selectedProjectFilter, setSelectedProjectFilter] = useState(projectFilter || 'all');
  const [assignedUserFilter, setAssignedUserFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    title: '',
    project_id: projectFilter || '',
    description: '',
    status: 'todo',
    priority: 'long_term',
    task_type: 'internal',
    assigned_to: [],
    deadline: '',
    estimated_minutes: '',
    checklist: [],
  });
  const [newCheckItem, setNewCheckItem] = useState('');

  const fetchData = async () => {
    try {
      const params = {};
      if (selectedProjectFilter && selectedProjectFilter !== 'all') {
        params.project_id = selectedProjectFilter;
      } else if (projectStatusFilter && projectStatusFilter !== 'all') {
        params.project_status = projectStatusFilter;
      }
      
      const [tasksRes, projectsRes, usersRes] = await Promise.all([
        getTasks(params),
        getProjects({}),
        getUserNames().catch(() => ({ data: [] })),
      ]);
      setTasks(tasksRes.data);
      setProjects(projectsRes.data);
      setUsers(usersRes.data || []);
    } catch (error) {
      toast.error('Chyba při načítání úkolů');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [projectStatusFilter, selectedProjectFilter]);

  // Handle task click to open detail dialog
  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setDetailDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { 
        ...formData,
        deadline: formData.deadline || null,
        assigned_to: formData.assigned_to.length > 0 ? formData.assigned_to : null,
        estimated_minutes: formData.estimated_minutes ? parseInt(formData.estimated_minutes) : null,
      };

      if (editingTask) {
        await updateTask(editingTask.id, data);
        toast.success('Úkol aktualizován');
      } else {
        await createTask(data);
        toast.success('Úkol vytvořen');
      }
      setDialogOpen(false);
      setEditingTask(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při ukládání');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      project_id: projectFilter || '',
      description: '',
      status: 'todo',
      priority: 'long_term',
      task_type: 'internal',
      assigned_to: [],
      deadline: '',
      estimated_minutes: '',
      checklist: [],
    });
    setNewCheckItem('');
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      project_id: task.project_id,
      description: task.description || '',
      status: task.status,
      priority: task.priority || 'long_term',
      task_type: task.task_type || 'internal',
      assigned_to: task.assigned_to || [],
      deadline: task.deadline || '',
      estimated_minutes: task.estimated_minutes?.toString() || '',
      checklist: task.checklist || [],
    });
    setNewCheckItem('');
    setDialogOpen(true);
  };

  const addCheckItem = () => {
    if (!newCheckItem.trim()) return;
    setFormData(f => ({
      ...f,
      checklist: [...f.checklist, { id: crypto.randomUUID(), text: newCheckItem.trim(), done: false }],
    }));
    setNewCheckItem('');
  };

  const removeCheckItem = (id) => setFormData(f => ({ ...f, checklist: f.checklist.filter(i => i.id !== id) }));
  const toggleCheckItem = (id) => setFormData(f => ({ ...f, checklist: f.checklist.map(i => i.id === id ? { ...i, done: !i.done } : i) }));

  const handleDelete = async () => {
    if (!taskToDelete) return;
    try {
      await deleteTask(taskToDelete.id);
      toast.success('Úkol smazán');
      fetchData();
    } catch (error) {
      toast.error('Chyba při mazání');
    }
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await updateTask(task.id, { status: newStatus });
      fetchData();
    } catch (error) {
      toast.error('Chyba při změně stavu');
    }
  };

  const toggleUserAssignment = (userId) => {
    setFormData(prev => ({
      ...prev,
      assigned_to: prev.assigned_to.includes(userId)
        ? prev.assigned_to.filter(id => id !== userId)
        : [...prev.assigned_to, userId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'todo': return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'done': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getPriorityInfo = (priority) => {
    switch (priority) {
      case 'urgent': return { label: 'Ihned k vyřešení', color: 'text-red-600', icon: AlertTriangle };
      case 'long_term': return { label: 'Dlouhodobý', color: 'text-blue-600', icon: Timer };
      default: return { label: 'Dlouhodobý', color: 'text-gray-600', icon: Timer };
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (searchTerm && !task.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !task.project_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (assignedUserFilter !== 'all' && !(task.assigned_to || []).includes(assignedUserFilter)) return false;
    return true;
  });

  const tasksByStatus = {
    todo: filteredTasks.filter(t => t.status === 'todo'),
    in_progress: filteredTasks.filter(t => t.status === 'in_progress'),
    done: filteredTasks.filter(t => t.status === 'done'),
  };

  // Filter projects by status for project dropdown
  const filteredProjects = projectStatusFilter && projectStatusFilter !== 'all'
    ? projects.filter(p => p.status === projectStatusFilter)
    : projects;

  // Refresh tasks (for TaskBoard callback)
  const handleTasksChange = () => {
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="font-oswald text-xl uppercase tracking-wide">Načítání...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">Úkoly</h1>
          <p className="font-serif text-gray-600 mt-1">Správa úkolů napříč projekty</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex border-2 border-black">
            <button
              onClick={() => setViewMode('board')}
              className={`p-2 ${viewMode === 'board' ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'}`}
              title="Board view"
            >
              <LayoutGrid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'}`}
              title="List view"
            >
              <List size={18} />
            </button>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#F4E600] text-black font-oswald uppercase tracking-wide border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
                onClick={() => { setEditingTask(null); resetForm(); }}
                data-testid="add-task-btn"
              >
                <Plus size={18} className="mr-2" />
                Nový úkol
              </Button>
            </DialogTrigger>
            <DialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-oswald text-xl uppercase tracking-tight">
                  {editingTask ? 'Upravit úkol' : 'Nový úkol'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-2">

                {/* Název */}
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Název úkolu *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    placeholder="Co je potřeba udělat?"
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                    data-testid="task-title-input"
                    autoFocus
                  />
                </div>

                {/* Projekt + Typ */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-oswald uppercase text-xs tracking-wide">Projekt *</Label>
                    <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
                      <SelectTrigger className="border-2 border-gray-200 rounded-none" data-testid="task-project-select">
                        <SelectValue placeholder="Vyberte projekt" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.filter(p => p.status === 'active' || p.status === 'future').map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}{p.status === 'future' ? ' (budoucí)' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-oswald uppercase text-xs tracking-wide">Typ</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, task_type: 'internal' })}
                        className={`flex items-center justify-center gap-1.5 py-2 border-2 font-oswald uppercase text-xs tracking-wide transition-all ${
                          formData.task_type === 'internal'
                            ? 'bg-black text-white border-black'
                            : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        <Building2 size={13} /> Interní
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, task_type: 'external' })}
                        className={`flex items-center justify-center gap-1.5 py-2 border-2 font-oswald uppercase text-xs tracking-wide transition-all ${
                          formData.task_type === 'external'
                            ? 'bg-[#006CB6] text-white border-[#006CB6]'
                            : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        <Globe size={13} /> Externí
                      </button>
                    </div>
                  </div>
                </div>

                {/* Status + Deadline + Odhad */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="font-oswald uppercase text-xs tracking-wide">Status</Label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                      <SelectTrigger className="border-2 border-gray-200 rounded-none">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todo">K řešení</SelectItem>
                        <SelectItem value="in_progress">Rozpracováno</SelectItem>
                        <SelectItem value="done">Hotovo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
                      <Calendar size={11} /> Deadline
                    </Label>
                    <Input
                      type="date"
                      value={formData.deadline}
                      onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                      className="border-2 border-gray-200 focus:border-black rounded-none"
                      data-testid="task-deadline-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
                      <Target size={11} /> Odhad (min)
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.estimated_minutes}
                      onChange={(e) => setFormData({ ...formData, estimated_minutes: e.target.value })}
                      className="border-2 border-gray-200 focus:border-black rounded-none"
                      placeholder="120"
                      data-testid="task-estimated-input"
                    />
                  </div>
                </div>

                {/* Popis */}
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Popis</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    placeholder="Podrobnější popis úkolu, kontext, poznámky…"
                    className="border-2 border-gray-200 focus:border-black rounded-none resize-none"
                    data-testid="task-description-input"
                  />
                </div>

                {/* Checklist */}
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
                    <Target size={11} /> To-do seznam
                    {formData.checklist.length > 0 && (
                      <span className="ml-1 text-gray-400 font-serif normal-case tracking-normal">
                        {formData.checklist.filter(i => i.done).length}/{formData.checklist.length}
                      </span>
                    )}
                  </Label>
                  {formData.checklist.length > 0 && (
                    <div className="border-2 border-gray-200 divide-y divide-gray-100">
                      {formData.checklist.map((item) => (
                        <div key={item.id} className="flex items-center gap-3 px-3 py-2">
                          <Checkbox
                            checked={item.done}
                            onCheckedChange={() => toggleCheckItem(item.id)}
                            className="flex-shrink-0"
                          />
                          <span className={`flex-1 font-serif text-sm ${item.done ? 'line-through text-gray-400' : ''}`}>
                            {item.text}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeCheckItem(item.id)}
                            className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      value={newCheckItem}
                      onChange={(e) => setNewCheckItem(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCheckItem(); } }}
                      placeholder="Přidat položku… (Enter)"
                      className="border-2 border-gray-200 focus:border-black rounded-none text-sm"
                    />
                    <Button
                      type="button"
                      onClick={addCheckItem}
                      variant="outline"
                      className="border-2 border-gray-200 rounded-none px-3 flex-shrink-0"
                    >
                      <Plus size={16} />
                    </Button>
                  </div>
                </div>

                {/* Přiřazení uživatelé */}
                {users.length > 0 && (
                  <div className="space-y-2">
                    <Label className="font-oswald uppercase text-xs tracking-wide">Přiřazení uživatelé</Label>
                    <div className="flex flex-wrap gap-2">
                      {users.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => toggleUserAssignment(u.id)}
                          className={`px-3 py-1.5 border-2 font-oswald uppercase text-xs tracking-wide transition-all ${
                            formData.assigned_to.includes(u.id)
                              ? 'bg-black text-white border-black'
                              : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                          }`}
                        >
                          {u.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none border-2 font-oswald uppercase">
                    Zrušit
                  </Button>
                  <Button
                    type="submit"
                    className="bg-black text-white font-oswald uppercase tracking-wide rounded-none hover:bg-gray-800 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
                    data-testid="task-submit-btn"
                  >
                    {editingTask ? 'Uložit změny' : 'Vytvořit úkol'}
                  </Button>
                </div>
              </form>
            </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Hledat úkoly..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-2 border-gray-200 focus:border-black rounded-none font-serif"
            data-testid="search-tasks"
          />
        </div>
        <Select value={projectStatusFilter} onValueChange={(v) => { setProjectStatusFilter(v); setSelectedProjectFilter('all'); }}>
          <SelectTrigger className="w-full sm:w-48 border-2 border-gray-200 rounded-none" data-testid="filter-project-status">
            <SelectValue placeholder="Stav projektů" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Všechny projekty</SelectItem>
            <SelectItem value="active">Aktivní projekty</SelectItem>
            <SelectItem value="potential">Potenciální projekty</SelectItem>
            <SelectItem value="future">Budoucí projekty</SelectItem>
            <SelectItem value="paused">Pozastavené projekty</SelectItem>
            <SelectItem value="completed">Dokončené projekty</SelectItem>
          </SelectContent>
        </Select>
        <Select value={selectedProjectFilter} onValueChange={setSelectedProjectFilter}>
          <SelectTrigger className="w-full sm:w-56 border-2 border-gray-200 rounded-none" data-testid="filter-project">
            <SelectValue placeholder="Konkrétní projekt" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Všechny</SelectItem>
            {filteredProjects.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={assignedUserFilter} onValueChange={setAssignedUserFilter}>
          <SelectTrigger className="w-full sm:w-48 border-2 border-gray-200 rounded-none">
            <SelectValue placeholder="Přiřazeno komu" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Všichni</SelectItem>
            {users.map((u) => (
              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Task Board - Trello-like with drag & drop */}
      {viewMode === 'board' ? (
        <TaskBoard 
          tasks={filteredTasks} 
          projectId={selectedProjectFilter !== 'all' ? selectedProjectFilter : null}
          onTasksChange={handleTasksChange}
          onTaskClick={handleTaskClick}
        />
      ) : (
        /* Kanban Board - List view */
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* To Do Column */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b-2 border-black">
              <div className="w-3 h-3 bg-gray-400 rounded-full" />
              <h2 className="font-oswald text-lg uppercase tracking-tight">K řešení</h2>
              <span className="text-sm text-gray-500 font-serif">({tasksByStatus.todo.length})</span>
            </div>
            {tasksByStatus.todo.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onEdit={handleEdit}
                onDelete={(t) => { setTaskToDelete(t); setDeleteDialogOpen(true); }}
                onStatusChange={handleStatusChange}
                getStatusColor={getStatusColor}
                getPriorityInfo={getPriorityInfo}
              />
            ))}
          </div>

          {/* In Progress Column */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b-2 border-black">
              <div className="w-3 h-3 bg-blue-500 rounded-full" />
              <h2 className="font-oswald text-lg uppercase tracking-tight">Rozpracováno</h2>
              <span className="text-sm text-gray-500 font-serif">({tasksByStatus.in_progress.length})</span>
            </div>
            {tasksByStatus.in_progress.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onEdit={handleEdit}
                onDelete={(t) => { setTaskToDelete(t); setDeleteDialogOpen(true); }}
                onStatusChange={handleStatusChange}
                getStatusColor={getStatusColor}
                getPriorityInfo={getPriorityInfo}
              />
            ))}
          </div>

          {/* Done Column */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b-2 border-black">
              <div className="w-3 h-3 bg-green-500 rounded-full" />
              <h2 className="font-oswald text-lg uppercase tracking-tight">Hotovo</h2>
              <span className="text-sm text-gray-500 font-serif">({tasksByStatus.done.length})</span>
            </div>
            {tasksByStatus.done.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onEdit={handleEdit}
                onDelete={(t) => { setTaskToDelete(t); setDeleteDialogOpen(true); }}
                onStatusChange={handleStatusChange}
                getStatusColor={getStatusColor}
                getPriorityInfo={getPriorityInfo}
              />
            ))}
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-oswald uppercase">Smazat úkol?</AlertDialogTitle>
            <AlertDialogDescription className="font-serif">
              Opravdu chcete smazat úkol &quot;{taskToDelete?.title}&quot;? Tato akce je nevratná.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-oswald uppercase rounded-none border-2 border-gray-200">
              Zrušit
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 font-oswald uppercase rounded-none hover:bg-red-600"
            >
              Smazat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Task Detail Dialog */}
      <TaskDetailDialog
        task={selectedTask}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        onTaskUpdated={fetchData}
        projects={projects}
      />
    </div>
  );
};

// Task Card Component
const TaskCard = ({ task, onEdit, onDelete, onStatusChange, getStatusColor, getPriorityInfo }) => {
  const priorityInfo = getPriorityInfo(task.priority);
  const PriorityIcon = priorityInfo.icon;
  const isOverdue = task.deadline && new Date(task.deadline) < new Date() && task.status !== 'done';

  return (
    <div
      className="bg-white border-2 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all group"
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-oswald font-bold tracking-tight line-clamp-2">{task.title}</h3>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit(task)}
            className="p-1 hover:bg-gray-100"
            data-testid={`edit-task-${task.id}`}
          >
            <Edit size={14} />
          </button>
          <button
            onClick={() => onDelete(task)}
            className="p-1 hover:bg-red-100 text-red-500"
            data-testid={`delete-task-${task.id}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      
      <div className="text-xs text-gray-500 font-serif mb-2">
        {task.project_name} • {task.client_name}
      </div>

      <div className="flex items-center gap-2 mb-2">
        <span className={`flex items-center gap-1 text-xs font-oswald ${priorityInfo.color}`}>
          <PriorityIcon size={12} />
          {priorityInfo.label}
        </span>
      </div>

      {task.assigned_names && task.assigned_names.length > 0 && (
        <div className="text-xs text-gray-500 font-serif mb-2">
          Přiřazeno: {task.assigned_names.join(', ')}
        </div>
      )}

      {task.deadline && (
        <div className={`flex items-center gap-1 text-xs font-serif mb-2 ${isOverdue ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
          <Calendar size={12} />
          Deadline: {task.deadline}
        </div>
      )}

      {task.total_time_minutes > 0 && (
        <div className="flex items-center gap-1 text-xs text-gray-500 font-serif mb-2">
          <Clock size={12} />
          {formatTime(task.total_time_minutes)}
        </div>
      )}

      {/* Quick status change */}
      <div className="flex gap-1 pt-2 border-t border-gray-200">
        {['todo', 'in_progress', 'done'].map((status) => (
          <button
            key={status}
            onClick={() => onStatusChange(task, status)}
            className={`flex-1 text-xs font-oswald uppercase py-1 border transition-colors ${
              task.status === status
                ? getStatusColor(status)
                : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            {status === 'todo' ? 'Todo' : status === 'in_progress' ? 'WIP' : 'Done'}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Tasks;
